import React, { useState, useEffect, useRef, forwardRef } from "react";
import clsx from 'clsx';
import {
  Card, CardContent,
} from '@material-ui/core';
import { withStyles } from '@material-ui/styles';

import withScrollListener from "../../../../../hocs/with_scroll_listener.js";
import { compose } from 'redux';

import I18n from "../../../../../base/i18n.js.jsx";
import { itemWithKeys } from "../../../../../utils/utils.js.jsx";
import FormFields from "../../../../../base/form/form_fields.js.jsx";
import HtmlContent from "../../../../../base/components/html_content.js.jsx";

import { AdminProjectModel } from "../../../../../models/projects/admin/project_model.js.jsx";

import NavigationTabContent from "../../../components/navigation_tab_content.js";

import pageStyles from "../../../../styles/page_styles.js.jsx";
import { infoPageStyles } from "../../../base_info_page.js.jsx";

const styles = theme => ({
  developerDescription: {
    marginTop: "20px"
  },
  developerDescriptionLabel: {
    marginTop: "10px",
  },
  developerDescriptionContent: {
    "& p": {
      marginBottom: "5px"
    }
  }
});

const InvisibleMountedKey = "InvisibleMountedKey"
const VisibleMountedKey   = "VisibleMountedKey"

const ProjectInfoTab = forwardRef((props, ref) => {

  const {
    classes,
    project = {},
    validations = {},
    scrollToElement
  } = props;
  const { onItemChanged, visible } = props;

  const [developer, setDeveloper] = useState(project.developer);
  const [errorData, setErrorData] = useState(props.errorData);
  const [tabKey, setTabKey]       = useState(visible ? VisibleMountedKey : InvisibleMountedKey);

  const formFields = AdminProjectModel.formFields({
    item: project,
    validations: validations
  });

  const mainInfoFields = ["name", "subtitle", "projectImages", "highlights", "bonusRates", "description", "listingOrder", "mediationType"];
  const mainInfoFieldsRef = useRef();

  const investmentsInfoFields = ["process", "beforeDevelopmentInterestRate", "annualInterestRate", "minInvestmentAmount", "ratingMark", "paidToDeveloperAt", "concludedAt", "secupayContractId"];
  const investmentsInfoFieldsRef = useRef();

  const datesInfoFields = ["executionTimeFrom", "executionTimeTo", "endDateFrom", "endDateTo", "endDate"];
  const datesInfoFieldsRef = useRef();

  const addressInfoFields = ["address"];
  const addressInfoFieldsRef = useRef();

  const developerInfoFields = ["developer"];
  const developerInfoFieldsRef = useRef();

  const companyInfoFields = ["company"];
  const companyInfoFieldsRef = useRef();

  const moneyInfoFields = ["cost", "amountOfOwnCapital", "amountOfBankCredit", "overinvestmentLimit"];
  const moneyInfoFieldsRef = useRef();

  const staticPartFields = ["staticPartOfContractPdf"];
  const staticPartFieldsRef = useRef();

  const documentsFields = ["documents"];
  const documentsFieldsRef = useRef();

  const allFields = [
    ...mainInfoFields,
    ...investmentsInfoFields,
    ...datesInfoFields,
    ...addressInfoFields,
    ...developerInfoFields,
    ...companyInfoFields,
    ...moneyInfoFields,
    ...staticPartFields,
    ...documentsFields
  ];

  const fieldsGroupRefs = [
    mainInfoFieldsRef,
    investmentsInfoFieldsRef,
    datesInfoFieldsRef,
    addressInfoFieldsRef,
    developerInfoFieldsRef,
    companyInfoFieldsRef,
    moneyInfoFieldsRef,
    staticPartFieldsRef,
    documentsFieldsRef
  ]

  const allFieldsRefs = useRef();

  useEffect(() => {
    allFieldsRefs.current = {};
    fieldsGroupRefs.forEach((fieldsGroup) => {
      const current = fieldsGroup.current || {};
      const refs = current.fieldRefs;
      if(refs) {
        allFieldsRefs.current = {
          ...allFieldsRefs.current,
          ...refs
        }
      }
    })
  }, [])

  useEffect(() => {
    const { errorData } = props;
    setErrorData(errorData)
    if(!errorData) {
      return;
    }

    let { fields: fieldsErrorData = {} } = errorData;

    formFields.forEach(field => {
      if(field.type !== "nestedModelsArray" && field.type !== "nestedModel") {
        return;
      }
      if(!errorData[field.name]) {
        return;
      }
      fieldsErrorData[field.name] = errorData[field.name];
    })

    let firstErrorFieldRef;
    allFields.forEach((fieldName) => {
      if(firstErrorFieldRef) {
        return;
      }
      let fieldErrorInfo = fieldsErrorData[fieldName];
      let fieldRef       = allFieldsRefs.current[fieldName];

      if(fieldErrorInfo && fieldRef) {
        firstErrorFieldRef = fieldRef
      }
    })
    if(!firstErrorFieldRef) {
      return;
    }

    if(scrollToElement) {
      scrollToElement(firstErrorFieldRef)
    }

  }, [props.errorData]);

  useEffect(() => {
    if(tabKey === VisibleMountedKey) {
      return;
    }

    if(visible && tabKey === InvisibleMountedKey) {
      setTabKey(VisibleMountedKey);
    }
  }, [visible]);


  const onChange = (changedItem, changedField) => {
    if(changedField.name === "developer") {
      setDeveloper({ ...changedItem.developer });
    }
    onItemChanged(changedItem, changedField);
  }

  return (
    <NavigationTabContent visible = { props.visible }
                          key = { tabKey } >
      <Card className = { clsx(classes.pageData, classes.pageDataCard) } >
        <CardContent className = { classes.pageDataContent } >
          <div className = { classes.cardTitle } >
            { I18n.t("admin.projects.project.field_group.main_info")}
          </div>
          <FormFields formFields = { formFields.filter(field => mainInfoFields.includes(field.name)) }
                      item = { itemWithKeys(project, mainInfoFields) }
                      errorData = { errorData }
                      onItemChange = { onChange }
                      defaultWeight = { 6 }
                      ref = { mainInfoFieldsRef }
                      />
        </CardContent>
      </Card>

      <Card className = { clsx(classes.pageData, classes.pageDataCard) } >
        <CardContent className = { classes.pageDataContent } >
          <div className = { classes.cardTitle } >
            { I18n.t("admin.projects.project.field_group.investments")}
          </div>
          <FormFields formFields = { formFields.filter(field => investmentsInfoFields.includes(field.name)) }
                      item = { itemWithKeys(project, investmentsInfoFields) }
                      errorData = { errorData }
                      onItemChange = { onChange }
                      defaultWeight = { 6 }
                      ref = { investmentsInfoFieldsRef }
                      />
        </CardContent>
      </Card>

      <Card className = { clsx(classes.pageData, classes.pageDataCard) } >
        <CardContent className = { classes.pageDataContent } >
          <div className = { classes.cardTitle } >
            { I18n.t("admin.projects.project.field_group.dates")}
          </div>
          <FormFields formFields = { formFields.filter(field => datesInfoFields.includes(field.name)) }
                      item = { itemWithKeys(project, datesInfoFields) }
                      errorData = { errorData }
                      onItemChange = { onChange }
                      defaultWeight = { 6 }
                      ref = { datesInfoFieldsRef }
                      />
        </CardContent>
      </Card>

      <Card className = { clsx(classes.pageData, classes.pageDataCard) } >
        <CardContent className = { classes.pageDataContent } >
          <div className = { classes.cardTitle } >
            { I18n.t("admin.projects.project.field_group.address")}
          </div>
          <FormFields formFields = { formFields.filter(field => addressInfoFields.includes(field.name)) }
                      item = { itemWithKeys(project, addressInfoFields) }
                      errorData = { errorData }
                      onItemChange = { onChange }
                      defaultWeight = { 6 }
                      ref = { addressInfoFieldsRef }
                      />
        </CardContent>
      </Card>

      <Card className = { clsx(classes.pageData, classes.pageDataCard) } >
        <CardContent className = { classes.pageDataContent } >
          <div className = { classes.cardTitle } >
            { I18n.t("admin.projects.project.field_group.company")}
          </div>
          <FormFields formFields = { formFields.filter(field => companyInfoFields.includes(field.name)) }
                      item = { itemWithKeys(project, companyInfoFields) }
                      errorData = { errorData }
                      onItemChange = { onChange }
                      defaultWeight = { 6 }
                      ref = { companyInfoFieldsRef }
                      />
        </CardContent>
      </Card>

      <Card className = { clsx(classes.pageData, classes.pageDataCard) } >
        <CardContent className = { classes.pageDataContent } >
          <div className = { classes.cardTitle } >
            { I18n.t("admin.projects.project.field_group.developer")}
          </div>
          <FormFields formFields = { formFields.filter(field => developerInfoFields.includes(field.name)) }
                      item = { itemWithKeys(project, developerInfoFields) }
                      errorData = { errorData }
                      onItemChange = { onChange }
                      defaultWeight = { 6 }
                      ref = { developerInfoFieldsRef }
                      />
          {
            developer && developer.description &&
            <div className = { classes.developerDescription } >
              <HtmlContent className = { classes.developerDescriptionContent }>
                { developer.description }
              </HtmlContent>
            </div>
          }
        </CardContent>
      </Card>

      <Card className = { clsx(classes.pageData, classes.pageDataCard) } >
        <CardContent className = { classes.pageDataContent } >
          <div className = { classes.cardTitle } >
            { I18n.t("admin.projects.project.field_group.money")}
          </div>
          <FormFields formFields = { formFields.filter(field => moneyInfoFields.includes(field.name)) }
                      item = { itemWithKeys(project, moneyInfoFields) }
                      errorData = { errorData }
                      onItemChange = { onChange }
                      defaultWeight = { 6 }
                      ref = { moneyInfoFieldsRef }
                      />
        </CardContent>
      </Card>

      <Card className = { clsx(classes.pageData, classes.pageDataCard) } >
        <CardContent className = { classes.pageDataContent } >
          <div className = { classes.cardTitle } >
            { I18n.t("admin.projects.project.field_group.static_part_of_contract")}
          </div>
          <FormFields formFields = { formFields.filter(field => staticPartFields.includes(field.name)) }
                      item = { itemWithKeys(project, staticPartFields) }
                      errorData = { errorData }
                      onItemChange = { onChange }
                      defaultWeight = { 6 }
                      ref = { staticPartFieldsRef }
                      />
        </CardContent>
      </Card>

      <Card className = { clsx(classes.pageData, classes.pageDataCard) } >
        <CardContent className = { classes.pageDataContent } >
          <div className = { classes.cardTitle } >
            { I18n.t("admin.projects.project.field_group.documents")}
          </div>
          <FormFields formFields = { formFields.filter(field => documentsFields.includes(field.name)) }
                      item = { itemWithKeys(project, documentsFields) }
                      errorData = { errorData }
                      onItemChange = { onChange }
                      defaultWeight = { 6 }
                      ref = { documentsFieldsRef }
                      />
        </CardContent>
      </Card>
    </NavigationTabContent>
  );
});

ProjectInfoTab.defaultProps = {
}

export default compose(
  withScrollListener,
  withStyles(
    (theme) => ({
      ...pageStyles(theme),
      ...infoPageStyles(theme),
      ...styles(theme),
    }),
    { withTheme: true }
  )
)(ProjectInfoTab);
